/**
 * Phase 17 Plan 17-02 — KpiTemplatesListPage
 *
 * Route: /kpi-templates
 * Permission gate: kpis.manage_templates (Admin-only).
 *
 * Permission gating is now route-level via <RequirePermission> in
 * `routes/index.tsx` (REVIEW FF-025). The old in-page
 * `useEffect → navigate('/dashboard')` pattern rendered one frame of
 * the page (and fired `useKpiTemplatesQuery`) before redirecting, so
 * it was removed.
 *
 * DataTable with Name / Unit / Description + row-actions (Edit, Delete).
 * "Create Template" button.
 */

import { useState, type JSX } from 'react';

import { CreateKpiTemplateDialog } from '@/components/kpi-templates/CreateKpiTemplateDialog';
import { DeleteKpiTemplateConfirmDialog } from '@/components/kpi-templates/DeleteKpiTemplateConfirmDialog';
import { EditKpiTemplateDialog } from '@/components/kpi-templates/EditKpiTemplateDialog';
import { formatKpiUnit } from '@/components/kpis/KpiUnitDisplay';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useKpiTemplatesQuery } from '@/hooks/useKpiTemplateQueries';
import type { KpiTemplate } from '@/types/kpi-template';

export function KpiTemplatesListPage(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<KpiTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KpiTemplate | null>(null);

  const templatesQuery = useKpiTemplatesQuery();
  const templates = templatesQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">KPI Templates</h1>
          <p className="text-muted-foreground">
            Global KPI template library. Apply to any Work Order to quickly create KPIs.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => { setCreateOpen(true); }}
          data-testid="create-template-btn"
        >
          Create Template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Templates</CardTitle>
        </CardHeader>
        <CardContent>
          {templatesQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="templates-loading">
              Loading templates…
            </p>
          )}

          {templatesQuery.isError && (
            <p className="text-sm text-destructive" data-testid="templates-error">
              Failed to load KPI templates.
            </p>
          )}

          <div className="overflow-x-auto">
          <Table data-testid="kpi-templates-list-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!templatesQuery.isLoading && templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-6">
                    <span className="text-muted-foreground" data-testid="templates-empty">
                      No KPI templates yet. Create one to get started.
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((t) => (
                  <TableRow key={t.id} data-testid={`template-row-${String(t.id)}`}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell>{formatKpiUnit(t.unit, t.customUnitText)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {t.description ?? '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditTarget(t); }}
                          data-testid={`template-edit-btn-${String(t.id)}`}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => { setDeleteTarget(t); }}
                          data-testid={`template-delete-btn-${String(t.id)}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      {createOpen && (
        <CreateKpiTemplateDialog open={createOpen} onOpenChange={setCreateOpen} />
      )}

      {editTarget !== null && (
        <EditKpiTemplateDialog
          open={true}
          onOpenChange={(o) => { if (!o) setEditTarget(null); }}
          template={editTarget}
        />
      )}

      {deleteTarget !== null && (
        <DeleteKpiTemplateConfirmDialog
          open={true}
          onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
          template={deleteTarget}
        />
      )}
    </div>
  );
}
